'use client';

import { ENGLISH_IQ_EVENTS } from '@repo/analytics';
import { useSession } from '@repo/auth';
import { buildEnglishIqPath, type EnglishIqDailyTargetMinutesType } from '@repo/config';
import type { IEnglishIqResultV2 } from '@repo/sdk';
import { getOrCreateVisitorId, trackEvent } from '@repo/web-chrome/lib/analytics';
import { Share2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { EnglishIqMessagesType } from '../../english-iq-messages-type';
import { saveEnglishIqV2Plan } from '../actions';
import { buildEnglishIqV2ShareUrl } from '../helper';
import { EnglishIqV2DeviceCta } from './english-iq-v2-device-cta';
import { EnglishIqV2Evidence } from './english-iq-v2-evidence';
import { EnglishIqV2Routine } from './english-iq-v2-routine';
import { EnglishIqV2TargetPicker } from './english-iq-v2-target-picker';

interface IEnglishIqV2ResultProps {
  locale: string;
  messages: EnglishIqMessagesType['v2']['result'];
  onRetake?: () => void;
  owned: boolean;
  result: IEnglishIqResultV2;
}

export function EnglishIqV2Result({
  locale,
  messages,
  onRetake,
  owned,
  result,
}: IEnglishIqV2ResultProps): React.JSX.Element {
  const { data: session } = useSession();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [target, setTarget] = useState<EnglishIqDailyTargetMinutesType>(10);
  const [shareLabel, setShareLabel] = useState(messages.share);

  useEffect(() => {
    headingRef.current?.focus();
    trackEvent(ENGLISH_IQ_EVENTS.resultRevealed, { score: result.score, surface: 'web_v2' });
    if (owned && result.shareId) {
      const localePrefix = locale === 'en' ? '' : `/${locale}`;
      const resultPath = `${localePrefix}/english-iq/r/${result.shareId}`;
      if (window.location.pathname !== resultPath) {
        window.history.replaceState(null, '', resultPath);
      }
    }
  }, [locale, owned, result.score, result.shareId]);

  const share = async (): Promise<void> => {
    if (!result.shareId) {
      return;
    }
    const referrerUserId = session?.user?.id ?? null;
    const url = buildEnglishIqV2ShareUrl({ locale, referrerUserId, shareId: result.shareId });
    trackEvent(ENGLISH_IQ_EVENTS.shareSheetOpened, { score: result.score, surface: 'web_v2' });
    if (navigator.share) {
      try {
        await navigator.share({ title: messages.title.replace('{subBand}', result.subBand), url });
        setShareLabel(messages.shared);
        trackEvent(ENGLISH_IQ_EVENTS.shareSuccessful, { method: 'native', surface: 'web_v2' });
      } catch {}
      return;
    }
    await navigator.clipboard.writeText(url);
    setShareLabel(messages.copied);
    trackEvent(ENGLISH_IQ_EVENTS.shareSuccessful, { method: 'copy', surface: 'web_v2' });
  };

  const chooseTarget = (minutes: EnglishIqDailyTargetMinutesType): void => {
    setTarget(minutes);
    trackEvent(ENGLISH_IQ_EVENTS.dailyTargetSelected, { minutes, surface: 'web_v2' });
    if (owned && result.shareId) {
      saveEnglishIqV2Plan({
        dailyTargetMinutes: minutes,
        shareId: result.shareId,
        visitorId: getOrCreateVisitorId(),
      }).catch(() => {});
    }
  };
  return (
    <main
      aria-live="polite"
      className="mx-auto min-h-[100svh] max-w-7xl px-4 py-10 sm:px-8 sm:py-16"
    >
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#ff9b50]">
        {messages.eyebrow}
      </p>
      <h1
        className="mt-3 text-balance text-4xl font-semibold tracking-[-0.045em] outline-none sm:text-7xl"
        ref={headingRef}
        tabIndex={-1}
      >
        {messages.title.replace('{subBand}', result.subBand)}
      </h1>
      <p className="mt-4 font-mono text-2xl font-black text-[#ff9b50] sm:text-4xl">
        {messages.iq.replace('{score}', String(result.score))}
      </p>
      <p className="mt-3 text-[#f7edda]/55">{messages.estimate}</p>
      {result.shareId ? (
        <button
          className="mt-7 inline-flex min-h-14 items-center gap-3 rounded-full bg-[#ff7a1a] px-7 font-extrabold text-[#160c06] shadow-[0_12px_40px_rgba(255,122,26,0.25)]"
          onClick={() => void share()}
          type="button"
        >
          <Share2 aria-hidden="true" className="size-5" />
          {shareLabel}
        </button>
      ) : null}
      <EnglishIqV2Evidence evidence={result.evidence} messages={messages} />
      <EnglishIqV2TargetPicker messages={messages} onSelect={chooseTarget} target={target} />
      <EnglishIqV2Routine messages={messages} subBand={result.subBand} target={target} />
      <EnglishIqV2DeviceCta messages={messages} owned={owned} result={result} />
      {onRetake ? (
        <a
          className="mt-8 min-h-12 font-bold text-[#ffb36b] underline-offset-4 hover:underline"
          href={buildEnglishIqPath(locale === 'en' ? undefined : locale)}
          onClick={onRetake}
        >
          {messages.retake}
        </a>
      ) : null}
    </main>
  );
}
